<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Sheet, SheetContent, SheetClose } from '@/components/ui/sheet'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'

interface SortOption {
	id: string
	label: string
	value?: string
}

const props = defineProps({
	isOpen: {
		type: Boolean,
		default: false,
	},
	sortOptions: {
		type: Array as () => SortOption[],
		required: true,
	},
	defaultValue: {
		type: String,
		default: 'recommended',
	},
	filterContent: {
		type: Object,
		required: true,
	},
})

const emit = defineEmits(['update:isOpen', 'apply'])

const selectedSort = ref(props.defaultValue)

watch(
	() => props.defaultValue,
	newValue => {
		selectedSort.value = newValue
	},
)

watch(
	() => props.isOpen,
	isOpen => {
		if (isOpen) {
			selectedSort.value = props.defaultValue
		}
	},
)

const handleClose = () => {
	emit('update:isOpen', false)
}

const handleApply = () => {
	emit('apply', selectedSort.value)
	emit('update:isOpen', false)
}
</script>

<template>
	<Sheet
		:open="isOpen"
		@update:open="(value: boolean) => emit('update:isOpen', value)"
	>
		<SheetContent side="bottom" class="p-0">
			<div class="flex flex-col rounded-t-lg bg-white">
				<header
					class="flex items-center justify-between border-b border-n-200 px-20px py-16px"
				>
					<div class="invisible w-16px"></div>
					<div class="typography-h5-str text-center">
						{{ filterContent.sort.title }}
					</div>
					<SheetClose @click="handleClose" class="p-0">
						<BaseIcon name="close" class="h-16px w-16px" />
					</SheetClose>
				</header>

				<main class="px-20px py-24px">
					<RadioGroup v-model="selectedSort" class="flex flex-col space-y-16px">
						<div
							v-for="option in sortOptions"
							:key="option.id"
							class="group cursor-pointer rounded-md hover:bg-n-50"
							@click="selectedSort = option.id"
						>
							<div class="flex items-center justify-between px-8px py-12px">
								<label
									:for="`sort-${option.id}`"
									class="typography-s1-reg flex-1 cursor-pointer select-none text-n-950"
								>
									{{ option.label }}
								</label>
								<RadioGroupItem :value="option.id" :id="`sort-${option.id}`" />
							</div>
						</div>
					</RadioGroup>
				</main>

				<footer class="mt-auto border-t border-n-200 p-16px">
					<BaseButton variant="primary" class="w-full" @click="handleApply">
						{{ filterContent.buttons.apply }}
					</BaseButton>
				</footer>
			</div>
		</SheetContent>
	</Sheet>
</template>
